import { useState, useCallback, useRef, useEffect } from 'react'
import { useWebSocket } from './useWebSocket'
import type { WSEvent } from './useWebSocket'
import { respondToAgent, getWizardAgentStatus } from '../api/client'
import type { ChatMessage } from '../types'

interface UseAgentChatOptions {
  agentId: string
  projectId: string
}

interface UseAgentChatReturn {
  messages: ChatMessage[]
  isAgentRunning: boolean
  isConnected: boolean
  sendFollowUp: (message: string) => Promise<void>
}

export function useAgentChat({ agentId, projectId }: UseAgentChatOptions): UseAgentChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isAgentRunning, setIsAgentRunning] = useState(true)
  const messageIdRef = useRef(0)
  const lastMessageTimeRef = useRef(Date.now())
  // Track whether this is the first WS connection (vs. a reconnect after disconnect)
  const hasConnectedOnceRef = useRef(false)

  const addMessage = useCallback((role: ChatMessage['role'], content: string, type: ChatMessage['type'] = 'text') => {
    messageIdRef.current += 1
    lastMessageTimeRef.current = Date.now()
    const msg: ChatMessage = {
      id: `msg-${messageIdRef.current}`,
      role,
      content,
      timestamp: new Date().toISOString(),
      type,
    }
    setMessages((prev) => [...prev, msg])
  }, [])

  const handleEvent = useCallback((event: WSEvent) => {
    const data = event.data as Record<string, unknown>

    if (event.type === 'agent.output' && data?.agent_id === agentId) {
      // Prefer parsed activity event summaries over raw JSON lines
      const events = data.events as Array<{ type: string; summary: string; tool?: string }> | undefined
      if (events && events.length > 0) {
        for (const evt of events) {
          // Skip system/internal events that aren't useful to users
          if (evt.type === 'system' || evt.type === 'error') continue
          if (evt.summary && evt.summary !== 'System message') {
            addMessage('assistant', evt.summary)
          }
        }
        return
      }

      // Fallback: use line/output/message but skip raw JSON and internal errors
      const raw = (data.line as string) || (data.output as string) || (data.message as string) || ''
      if (raw && !raw.trimStart().startsWith('{') && !raw.trimStart().startsWith('[')) {
        // Skip internal messages that shouldn't be shown to users
        const lower = raw.toLowerCase()
        if (lower.includes('failed to authenticate') || lower.includes('oauth token') || lower.includes('api error: 401')) {
          return
        }
        if (lower.includes('rate_limit_event') || lower.includes('"type":"system"')) {
          return
        }
        addMessage('assistant', raw)
      }
    }

    if (event.type === 'agent.started' && data?.agent_id === agentId) {
      setIsAgentRunning(true)
    }

    if (event.type === 'agent.stopped' && data?.agent_id === agentId) {
      setIsAgentRunning(false)
    }
  }, [agentId, addMessage])

  // Check agent status via API (handles missed WebSocket events)
  const checkAgentStatus = useCallback(async () => {
    try {
      const status = await getWizardAgentStatus(agentId)
      if (!status.running) {
        setIsAgentRunning(false)
      }
    } catch {
      // Network error or transient failure — do NOT change running state.
      // The agent.stopped WebSocket event is the authoritative signal.
      // Setting false here on errors caused premature "Build complete" banners.
    }
  }, [agentId])

  const handleConnect = useCallback(() => {
    // On WebSocket reconnect (not first connect), check if agent finished while disconnected.
    // Skip the first connect — the agent was just started and is definitely running.
    // Checking on first connect caused premature "stopped" detection when the API
    // returned an error before the agent was fully registered.
    if (hasConnectedOnceRef.current) {
      checkAgentStatus()
    }
    hasConnectedOnceRef.current = true
  }, [checkAgentStatus])

  const { isConnected } = useWebSocket({ onEvent: handleEvent, onConnect: handleConnect })

  // Poll agent status periodically as a safety net.
  // If no new messages arrive for 30s while supposedly running, check the API.
  useEffect(() => {
    if (!isAgentRunning) return
    const interval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current
      if (timeSinceLastMessage > 30000) {
        checkAgentStatus()
      }
    }, 15000)
    return () => clearInterval(interval)
  }, [isAgentRunning, checkAgentStatus])

  // Check status on initial mount (in case agent finished before component mounted)
  useEffect(() => {
    const timer = setTimeout(() => checkAgentStatus(), 3000)
    return () => clearTimeout(timer)
  }, [checkAgentStatus])

  // Track projectId to suppress lint warning (used for future follow-up context)
  const projectIdRef = useRef(projectId)
  useEffect(() => { projectIdRef.current = projectId }, [projectId])

  const sendFollowUp = useCallback(async (message: string) => {
    addMessage('user', message)
    try {
      await respondToAgent(agentId, message)
    } catch (err) {
      addMessage('system', `Failed to send: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error')
    }
  }, [agentId, addMessage])

  return { messages, isAgentRunning, isConnected, sendFollowUp }
}
